import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import landingHeroBg from '../../assets/images/index-bg-3.png';
import { findPlatformPlan } from '../../data/platformPlans';

const REDIRECT_SECONDS = 8;

// Success screen (Claude Design - Pantalla 04): centered card with gradient
// check, welcome + plan, progress bar and auto-redirect to the tenant portal.
function OnboardingFinished() {
  const reduxAccountName = useSelector(state => state.account.accountName);
  const history = useHistory();
  const location = useLocation();

  const stateData = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const isIframeMode = searchParams.get('format') === 'iframe';

  // Query params work as fallback so the screen is previewable/QA-able without
  // walking the whole payment flow: /success?account=demo&name=John&plan=pro
  // `account=test` shows the full redirect UI (spinner, progress bar, button)
  // but NEVER navigates - the visual test mode.
  const accountName = stateData.accountName || searchParams.get('account') || reduxAccountName;
  const isPreviewAccount = !stateData.accountName && searchParams.get('account') === 'test';
  const firstname = stateData.firstname || searchParams.get('name');
  const planId = stateData.planId || searchParams.get('plan');
  const plan = planId ? findPlatformPlan(planId) : null;
  const email = stateData.email || searchParams.get('email');
  const portalUrl = accountName ? `https://${accountName}.parrotfy.com` : null;
  const [redirecting, setRedirecting] = useState(Boolean(portalUrl) && !isIframeMode);

  // Any preview param is enough to render the screen (QA without the flow);
  // only a completely empty visit bounces back to the start.
  const hasPreviewParams = ['account', 'name', 'plan', 'email'].some(param => searchParams.has(param));
  const landingHeroStyle = !isIframeMode ? { '--landing-hero-bg-url': `url(${landingHeroBg})` } : {};

  useEffect(() => {
    if (!accountName && !hasPreviewParams) {
      history.push('/onboarding');
      return undefined;
    }
    if (!redirecting || isPreviewAccount) return undefined;

    const timer = setTimeout(() => {
      window.location.href = portalUrl;
    }, REDIRECT_SECONDS * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [accountName, redirecting]);

  const handleRedirect = () => {
    if (isPreviewAccount) return;
    setRedirecting(false);
    window.location.href = portalUrl;
  };

  return (
    <div
      className="onboarding-page landing-hero d-flex align-items-center justify-content-center"
      style={!isIframeMode ? landingHeroStyle : undefined}
    >
      {!isIframeMode && (
        <>
          <div className="landing-hero__bg" />
          <div className="landing-hero__wash" />
          <div className="landing-hero__darken" />
          <div className="landing-hero__tint" />
        </>
      )}
      <div className="pf-page__content py-5">
        <div className="pf-card pf-success-card">
          <span className="pf-success-card__check" aria-hidden="true">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
              <path d="M20 6 9 17l-5-5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div>
            <span className="pf-badge pf-badge--success">Cuenta creada</span>
          </div>
          <h1 className="pf-success-card__title">¡Bienvenido a Parrotfy{firstname ? `, ${firstname}` : ''}!</h1>
          <p className="pf-success-card__text">
            {plan ? (
              <>
                Tu cuenta con el plan <strong>Parrotfy {plan.name}</strong> está lista.
              </>
            ) : (
              'Tu cuenta está lista.'
            )}{' '}
            Te estamos llevando a tu portal.
          </p>

          {redirecting && (
            <>
              <div className="pf-success-card__redirect">
                <span className="pf-success-card__spinner" aria-hidden="true" />
                Redirigiendo a tu portal…
              </div>
              <div className="pf-success-card__progress" aria-hidden="true">
                <span className="pf-success-card__progress-bar" />
              </div>
            </>
          )}

          {portalUrl && (
            <button type="button" className="btn pf-btn pf-btn--gradient pf-btn--block" onClick={handleRedirect}>
              Ir al portal ahora →
            </button>
          )}

          <p className="pf-success-card__footnote">
            ¿No se redirige automáticamente? Usa el botón de arriba.
            {email ? (
              <>
                {' '}
                Te enviamos un correo a <strong>{email}</strong> para configurar tu contraseña.
              </>
            ) : (
              ' Revisa tu correo para configurar tu contraseña.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default OnboardingFinished;
